import { ReactElement, ReactNode } from 'react';
import { Redirect } from 'react-router';
import { useHasPermission } from '../api/authorization.provider';
import { buildMagicLinkFromSearchParams } from '../components/login/login.hooks';
import { useBeaconRouter } from '../route/route.hooks';
import { Route } from '../route/route.types';
import { useNavLocationHistoryLazy } from '../store/nav-history/nav-history.hooks';
import {
  useActiveWorkspace,
  useIsAuthenticated,
  useIsAuthenticatedLazy,
  useWorkspaceList,
} from '../store/workspace/workspace.hooks';
import { useQueryParameters } from '../util/router';
import { Page, PageProps } from './page';

export interface TenantRouteParams {
  tenantId: string;
}
interface ProtectedRouteProps extends PageProps {
  children?: ReactNode;
  computedMatch?: {
    params: TenantRouteParams;
  };
  /**
   * Assuming the user is authenticated, is the user
   * actually authorized to access this route?
   * Default is true.
   */
  isAllowed?: boolean;
}

const ProtectedRoute = (props: ProtectedRouteProps): ReactElement => {
  const { children, isAllowed = true, ...otherProps } = props;
  const { builder } = useBeaconRouter();

  // computedMatch could be fragile if we move this component out of
  // the switch in App.tsx.  If this 'breaks', it would only mean that
  // we wouldn't find an inactive tenant and redirect the user to login
  const authenticated = useIsAuthenticated(
    props.computedMatch?.params.tenantId
  );

  return (
    <Page
      {...otherProps}
      render={({ location }) =>
        authenticated && isAllowed ? (
          // If authenticated and authorized
          // then show the requested page.
          children
        ) : authenticated ? (
          // If authenticated but not authorized
          // for the requested page then redirect home.
          <Redirect
            to={{
              pathname: builder.getHomeRoute(),
            }}
          />
        ) : (
          // We have no idea who you are, log in!
          <Redirect
            to={{
              pathname: builder.getAuthLoginRoute(),
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const AdminProtectedRoute = (
  props: ProtectedRouteProps
): ReactElement | null => {
  const { isAllowed = true } = props;
  const isAdmin = useHasPermission('admin', 'tenant');

  // we need to block until the admin prop is defined
  if (isAdmin === undefined) {
    return null;
  }

  return <ProtectedRoute {...props} isAllowed={isAllowed && isAdmin} />;
};

const BillingManagerProtectedRoute = (
  props: ProtectedRouteProps
): ReactElement | null => {
  const { isAllowed = true } = props;
  const canReadPlans = useHasPermission('read', 'plan_subscription');

  // we need to block until the prop is defined
  if (canReadPlans === undefined) {
    return null;
  }

  return <ProtectedRoute {...props} isAllowed={isAllowed && canReadPlans} />;
};

const RedirectIfAuthenticated = (): ReactElement => {
  const activeWorkspace = useActiveWorkspace();
  const workspaces = useWorkspaceList();
  const getLastLocationForTenant = useNavLocationHistoryLazy();
  const checkAuthenticated = useIsAuthenticatedLazy();

  const { builder } = useBeaconRouter();

  const queryParams = useQueryParameters();

  const isMagicLink = queryParams.get('magic') === '1';
  const magicLink = isMagicLink
    ? buildMagicLinkFromSearchParams(queryParams)
    : undefined;

  if (activeWorkspace) {
    let workspaceDestination = activeWorkspace;

    if (isMagicLink && magicLink?.isValid()) {
      const username = magicLink.username;

      // if the magic link username is not the same as the active workspace username
      // then we need to navigate to the confirm page
      if (username !== activeWorkspace.user.username) {
        const magicLinkWorkspace = workspaces.find(
          (w) => w?.user?.username === username
        );

        if (
          magicLinkWorkspace !== undefined &&
          checkAuthenticated(magicLinkWorkspace.tenant.id)
        ) {
          workspaceDestination = magicLinkWorkspace;
        } else {
          return (
            <Redirect
              to={{
                pathname: builder.getAuthConfirmRoute(),
                search: queryParams.toString(),
              }}
            />
          );
        }
      }
    }

    const skipLastLocation = queryParams.get('skipLastLocation') ?? undefined;
    const lastLocation = getLastLocationForTenant(
      workspaceDestination.tenant.id
    );
    if (lastLocation && skipLastLocation === undefined) {
      let pathname = lastLocation.pathname;
      if (
        !pathname.startsWith(`/${workspaceDestination.tenant.id}`) &&
        !pathname.startsWith(Route.DIRECT_CONNECT)
      ) {
        pathname = `/${workspaceDestination.tenant.id}${lastLocation.pathname}`;
      }

      return <Redirect to={{ pathname, search: lastLocation?.search }} />;
    } else {
      return (
        <Redirect
          to={{
            pathname: builder.getHomeRoute(),
          }}
        />
      );
    }
  } else {
    if (isMagicLink) {
      return (
        <Redirect
          to={{
            pathname: builder.getAuthConfirmRoute(),
            search: queryParams.toString(),
          }}
        />
      );
    } else {
      return (
        <Redirect
          to={{
            pathname: builder.getAuthLoginRoute(),
          }}
        />
      );
    }
  }
};

export {
  ProtectedRoute,
  AdminProtectedRoute,
  BillingManagerProtectedRoute,
  RedirectIfAuthenticated,
};
